/**
 * The address this firm's client email goes out as.
 *
 * ===========================================================================
 * TWO WAYS TO GET THERE, ONE SCREEN
 * ===========================================================================
 * The address/Reply-To identity below was the original answer, and it still is one:
 * type an address, confirm it, and clients see the firm's name with replies landing on
 * that address — mail still physically leaves through this platform's own account,
 * because a bare `@gmail.com` or `@outlook.com` address can never be signed as by a
 * third party (DMARC/DKIM alignment; see the identity card's own copy below).
 *
 * Connecting Gmail directly (this page's top card) is a second, stronger option next to
 * it, not a replacement: an administrator signs the firm's own Gmail account in once,
 * and from then on Google's own servers send the mail, as that real address, with
 * nothing left to align. Neither path requires the other. A firm with its own domain
 * might prefer the identity path (or, once it has DKIM, a true From through it); a firm
 * on a plain Gmail address gets a true From only by connecting here.
 *
 * ===========================================================================
 * WHAT THE IDENTITY CARD HAS TO EXPLAIN, AND WHY IT IS AWKWARD
 * ===========================================================================
 * A firm asks for one thing — "send from our address" — and gets two answers, because
 * two independent things have to be true and only one of them is in their gift.
 *
 *   OWNERSHIP. Somebody opened a link sent to the address. That is the only proof that
 *   means anything, and it is enough for replies to reach the firm.
 *
 *   ALIGNMENT. DNS records authorise this platform's provider to sign mail for that
 *   domain. Confirming an address does not grant it. A `@gmail.com` address never
 *   will — Google publishes no record letting a third party sign as its users.
 *
 * The temptation is to hide the second one and just send as whatever was typed. That
 * fails silently: mail is accepted, then filtered, and the firm discovers it weeks
 * later when a client says they never got anything. A screen that is slightly harder
 * to read is much better than a feature that appears to work and does not.
 *
 * So the status line says what will actually happen to the next email, in those terms,
 * rather than reporting a state name.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type EmailIdentity,
  type EmailIdentityResponse,
  type GmailConnection,
  type GmailConnectionResponse,
  type GmailAuthorizeResponse,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Form,
  FormActions,
  FormInput,
  Skeleton,
  useToast,
  useZodForm,
} from '@legalcrm/ui';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const schema = z.object({
  address: z.string().email('Enter a valid email address'),
  display_name: z.string(),
});

type Values = z.infer<typeof schema>;

export function FirmEmailPage() {
  const identity = useApiQuery<EmailIdentityResponse>(
    qk.list('email-identity'),
    STAFF.organizationEmailIdentity,
  );
  const gmail = useApiQuery<GmailConnectionResponse>(
    qk.list('gmail-connection'),
    STAFF.organizationGmail,
  );
  const gmailConnection = gmail.data?.connection ?? null;

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <ConfirmFromLink />
      <GmailRedirectFeedback />
      <GmailCard connection={gmailConnection} loading={gmail.isLoading} />
      <CurrentState
        identity={identity.data?.identity ?? null}
        loading={identity.isLoading}
        gmailConnected={gmailConnection?.connected ?? false}
      />
      <AddressForm identity={identity.data?.identity ?? null} />
    </div>
  );
}

/**
 * What happens to the next email the firm's clients receive.
 *
 * Deliberately phrased as an outcome rather than a status. "Confirmed" tells an
 * administrator nothing about whether their client will see the firm's name.
 */
function CurrentState({
  identity,
  loading,
  gmailConnected,
}: {
  identity: EmailIdentity | null;
  loading: boolean;
  gmailConnected: boolean;
}) {
  if (loading) return null;

  // A connected Gmail account is the stronger answer and takes over the description of
  // "what happens next" — see the page's own top-of-file note on why these are two
  // paths rather than one. The card above already shows the connected address; this
  // card would otherwise repeat the (now irrelevant, since Gmail is what actually
  // sends) identity/Reply-To story underneath it.
  if (gmailConnected) {
    return (
      <Card>
        <CardHeader title="Right now" />
        <CardBody>
          <p className="text-sm text-ink-muted">
            Client email sends through your connected Gmail account, above. The address
            and confirmation below are not in use while that stays connected.
          </p>
        </CardBody>
      </Card>
    );
  }

  if (!identity) {
    return (
      <Card>
        <CardHeader title="Right now" />
        <CardBody>
          <p className="text-sm text-ink-muted">
            Your clients receive email from this platform’s address, with no mention of
            your firm and nowhere useful to reply. Set an address below, or connect
            Gmail above for a true From address straight away.
          </p>
        </CardBody>
      </Card>
    );
  }

  const { state, tone, detail } = describe(identity);

  return (
    <Card>
      <CardHeader title="Right now" />
      <CardBody>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={tone}>{state}</Badge>
          <code className="text-sm text-ink-muted">{identity.address}</code>
        </div>
        <p className="mt-2 text-sm text-ink-muted">{detail}</p>
      </CardBody>
    </Card>
  );
}

function describe(identity: EmailIdentity): {
  state: string;
  tone: 'success' | 'info' | 'warning';
  detail: string;
} {
  if (identity.usable_as_from) {
    return {
      state: 'Sending as your firm',
      tone: 'success',
      detail:
        'Client email is sent from this address. Replies come straight back to it.',
    };
  }
  if (identity.usable_as_reply_to) {
    return {
      state: 'Confirmed',
      tone: 'info',
      detail:
        'Your clients see your firm’s name, and their replies come to this address. ' +
        'The message itself is still sent from this platform’s domain — mail claiming ' +
        'to come from a Gmail or Outlook address but sent by somebody else is treated ' +
        'as spam, so we do not do it. To send truly as your firm you need your own ' +
        'domain; contact us once you have one and we will set up the DNS records.',
    };
  }
  if (identity.status === 'pending') {
    return {
      state: 'Waiting for confirmation',
      tone: 'warning',
      detail:
        'We sent a link to this address. Nothing changes until somebody opens it — ' +
        'until then your clients still receive email from this platform’s address. ' +
        'Check the spam folder, or set the address again to send a new link.',
    };
  }
  return {
    state: 'Not confirmed',
    tone: 'warning',
    detail: 'This address is not in use. Set it again to send a confirmation link.',
  };
}

function AddressForm({ identity }: { identity: EmailIdentity | null }) {
  const api = useApi();
  const { toast } = useToast();

  const form = useZodForm(schema, {
    values: {
      address: identity?.address ?? '',
      display_name: identity?.display_name ?? '',
    },
  });

  const save = useApiMutation<EmailIdentityResponse, Values>(
    {
      mutationFn: (values) =>
        api.post<EmailIdentityResponse>(`${STAFF.organizationEmailIdentity}/`, values),
      invalidates: [qk.all('email-identity')],
    },
    {
      onSuccess: (result) => {
        toast({
          title: 'Confirmation sent',
          description: `Open the link we sent to ${result.identity?.address} to finish.`,
          tone: 'success',
        });
      },
      onError: (error) => {
        toast({
          title: 'That address was not accepted',
          description:
            error instanceof ConflictError ? error.detail : 'Try again.',
          tone: 'warning',
        });
      },
    },
  );

  return (
    <Card>
      <CardHeader
        title="Your firm’s address"
        description="Where your clients' replies should go, and the name they see."
      />
      <CardBody>
        <Form
          form={form}
          ariaLabel="Firm email address"
          error={save.error}
          onSubmit={(values) => save.mutateAsync(values)}
        >
          <FormInput
            form={form}
            name="display_name"
            label="Name your clients see"
            hint="Your firm’s name as it should appear in their inbox."
            autoComplete="off"
          />
          <FormInput
            form={form}
            name="address"
            label="Email address"
            type="email"
            hint={
              // Said here rather than after they have typed it and waited. A Gmail
              // address is a perfectly good answer for now and it is worth saying so,
              // because the alternative reading of the status is that it failed.
              'A Gmail or Outlook address works — your clients will see your firm’s ' +
              'name and reply to you. Your own domain lets mail be sent as your firm ' +
              'outright.'
            }
            autoComplete="off"
          />
          {/*
            Changing the address discards the existing confirmation, which is not
            obvious from a form that is pre-filled with the current one.
          */}
          {identity?.usable_as_reply_to && (
            <p className="text-sm text-ink-muted">
              Changing this address means confirming the new one before it is used.
              Until then your clients receive email from this platform’s address.
            </p>
          )}
          <FormActions
            submitLabel={identity ? 'Save and send confirmation' : 'Send confirmation'}
            busy={save.isPending}
          />
        </Form>
      </CardBody>
    </Card>
  );
}

/**
 * Redeems the token from the emailed link.
 *
 * The link lands on this page with `?token=`, so the person arrives already looking at
 * the screen that explains what just happened rather than at a bare success message
 * with no context.
 */
function ConfirmFromLink() {
  const [params, setParams] = useSearchParams();
  const token = params.get('token');
  const api = useApi();
  const { toast } = useToast();
  const attempted = useRef<string | null>(null);

  const confirm = useApiMutation<EmailIdentityResponse, string>(
    {
      mutationFn: (value) =>
        api.post<EmailIdentityResponse>(
          `${STAFF.organizationEmailIdentityConfirm}/`,
          { token: value },
        ),
      invalidates: [qk.all('email-identity')],
    },
    {
      onSuccess: (result) =>
        toast({
          title: 'Address confirmed',
          description: `${result.identity?.address} is now used for your clients' replies.`,
          tone: 'success',
        }),
      onError: (error) =>
        toast({
          title: 'That link did not work',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'It may have expired. Set the address again to send a new one.',
          tone: 'warning',
        }),
    },
  );

  useEffect(() => {
    if (!token || attempted.current === token) return;
    // Once per token. Without the guard, a re-render retries a token the server has
    // already spent and reports a failure for something that worked.
    attempted.current = token;
    void confirm.mutateAsync(token).finally(() => {
      // Out of the URL either way: a spent token in an address bar gets bookmarked,
      // pasted into a chat, and retried.
      params.delete('token');
      setParams(params, { replace: true });
    });
  }, [token, confirm, params, setParams]);

  return null;
}

/**
 * Connect, reconnect or disconnect the firm's own Gmail account.
 *
 * Three states, matching what the backend actually does with each (see
 * `domains.orgconfig.gmail_oauth.resolve_adapter`):
 *
 *   not connected   — optional. Client email keeps sending through the identity/
 *                     Reply-To path below, exactly as it always has.
 *   connected       — the stronger path. A true From, sent by Google directly.
 *   revoked         — this firm opted in and it broke. Blocked outright until
 *                     reconnected — NOT quietly moved back to the path below, even
 *                     though that path is right there and still configured.
 */
function GmailCard({
  connection,
  loading,
}: {
  connection: GmailConnection | null;
  loading: boolean;
}) {
  const api = useApi();
  const { toast } = useToast();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const authorize = useApiMutation<GmailAuthorizeResponse, void>(
    {
      mutationFn: () => api.post<GmailAuthorizeResponse>(`${STAFF.organizationGmailAuthorize}/`),
    },
    {
      onSuccess: (result) => {
        // A full browser navigation, not a fetch — Google's consent screen is not part
        // of this application and cannot be reached with an Authorization header.
        window.location.href = result.authorize_url;
      },
      onError: (error) => {
        toast({
          title: 'Could not start connecting Gmail',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'Try again, or see docs/EMAIL_SETUP.md if this keeps happening.',
          tone: 'warning',
        });
      },
    },
  );

  const disconnect = useApiMutation<void, void>(
    {
      mutationFn: () => api.delete<void>(`${STAFF.organizationGmail}/`),
      invalidates: [qk.all('gmail-connection')],
    },
    {
      onSuccess: () => {
        setConfirmingDisconnect(false);
        toast({ title: 'Gmail disconnected', tone: 'success' });
      },
    },
  );

  if (loading) {
    return (
      <Card>
        <CardHeader title="Send through Gmail" />
        <CardBody>
          <Skeleton className="h-10 w-full" />
        </CardBody>
      </Card>
    );
  }

  if (connection?.status === 'revoked') {
    return (
      <Card>
        <CardHeader title="Send through Gmail" />
        <CardBody>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="danger">Needs reconnecting</Badge>
            <code className="text-sm text-ink-muted">{connection.address}</code>
          </div>
          <p className="mt-2 text-sm text-ink-muted">
            Google reported this connection no longer works — most often because access
            was removed at google.com/permissions, or the password changed. Client email
            is <strong>not being sent</strong> while this stays unreconnected; it does
            not fall back to any other address.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => authorize.mutate()} loading={authorize.isPending}>
              Reconnect Gmail
            </Button>
            {/* The way out when reconnecting is not possible right now (Google refuses,
                or the account is gone): removing the connection moves the firm back to
                the platform sender, deliberately and visibly, rather than silently. */}
            <Button variant="ghost" onClick={() => setConfirmingDisconnect(true)}>
              Disconnect
            </Button>
          </div>
        </CardBody>
        <ConfirmModal
          open={confirmingDisconnect}
          onClose={() => setConfirmingDisconnect(false)}
          title="Disconnect Gmail?"
          confirmLabel="Disconnect"
          destructive
          loading={disconnect.isPending}
          onConfirm={() => void disconnect.mutateAsync()}
          consequences={
            <p>
              Client email will no longer wait for {connection.address} to be reconnected.
              If an address is confirmed below, sending uses it (Reply-To only); otherwise
              it uses this platform’s own address. You can connect Gmail again at any time.
            </p>
          }
        />
      </Card>
    );
  }

  if (connection?.connected) {
    return (
      <Card>
        <CardHeader title="Send through Gmail" />
        <CardBody>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="success">Connected</Badge>
            <code className="text-sm text-ink-muted">{connection.address}</code>
          </div>
          <p className="mt-2 text-sm text-ink-muted">
            Client email is sent from this address by Google directly — a true From,
            with nothing to align.
          </p>
          <div className="mt-3">
            <Button variant="ghost" onClick={() => setConfirmingDisconnect(true)}>
              Disconnect
            </Button>
          </div>
        </CardBody>
        <ConfirmModal
          open={confirmingDisconnect}
          onClose={() => setConfirmingDisconnect(false)}
          title="Disconnect Gmail?"
          confirmLabel="Disconnect"
          destructive
          loading={disconnect.isPending}
          onConfirm={() => void disconnect.mutateAsync()}
          consequences={
            <p>
              Client email stops sending from {connection.address}. If an address is
              confirmed below, sending falls back to it (Reply-To only); otherwise it
              falls back to this platform’s own address.
            </p>
          }
        />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Send through Gmail"
        description="Connect the firm’s own Gmail account for a true From address, straight away."
      />
      <CardBody>
        <p className="text-sm text-ink-muted">
          An administrator signs in to the firm’s Gmail account once. From then on,
          client email — invitations, agreements, everything — sends from that real
          address, through Google’s own servers.
        </p>
        <div className="mt-3">
          <Button onClick={() => authorize.mutate()} loading={authorize.isPending}>
            Connect Gmail
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Reads the redirect back from `GmailCallbackView` (`?gmail=connected&address=…` or
 * `?gmail=error&reason=…`) and turns it into a toast, then cleans the URL — the same
 * pattern as `ConfirmFromLink`, for the same reason: a query parameter left in the
 * address bar gets bookmarked and re-shown on every later visit to this page.
 */
function GmailRedirectFeedback() {
  const [params, setParams] = useSearchParams();
  const { toast } = useToast();
  const handled = useRef(false);

  useEffect(() => {
    const outcome = params.get('gmail');
    if (!outcome || handled.current) return;
    handled.current = true;

    if (outcome === 'connected') {
      const address = params.get('address');
      toast({
        title: 'Gmail connected',
        description: address
          ? `Client email now sends from ${address}.`
          : 'Client email now sends through the connected account.',
        tone: 'success',
      });
    } else if (outcome === 'error') {
      toast({
        title: 'Could not connect Gmail',
        description: params.get('reason') || 'Try again from Settings.',
        tone: 'warning',
      });
    }

    params.delete('gmail');
    params.delete('address');
    params.delete('reason');
    setParams(params, { replace: true });
  }, [params, setParams, toast]);

  return null;
}
